import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { createCircle, joinByInviteCode } from '../services/circleService';
import { useCircle } from '../hooks/useCircle';

export default function CreateJoinCirclePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const { selectCircle } = useCircle();
  const [mode, setMode] = useState(searchParams.get('mode') || null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const circleId = await createCircle(currentUser.uid, name.trim(), userProfile);
      selectCircle(circleId);
      navigate('/art');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (code.length < 6) return;
    setLoading(true);
    setError(null);
    try {
      const circleId = await joinByInviteCode(code.trim(), currentUser.uid, userProfile);
      selectCircle(circleId);
      navigate('/art');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => mode ? setMode(null) : navigate('/circles')}>
          <ArrowLeft size={20} />
        </BackBtn>
        <HeaderTitle>{mode === 'create' ? 'Create Circle' : mode === 'join' ? 'Join Circle' : 'Get Started'}</HeaderTitle>
        <Spacer />
      </Header>

      {!mode && (
        <ModeSelect>
          <ModeCard onClick={() => setMode('create')}>
            <ModeEmoji>✨</ModeEmoji>
            <ModeTitle>Create a Circle</ModeTitle>
            <ModeDesc>Start a new art group with your family</ModeDesc>
          </ModeCard>
          <ModeCard onClick={() => setMode('join')}>
            <ModeEmoji>🔗</ModeEmoji>
            <ModeTitle>Join a Circle</ModeTitle>
            <ModeDesc>Enter an invite code to join</ModeDesc>
          </ModeCard>
        </ModeSelect>
      )}

      {mode === 'create' && (
        <Form>
          <Label>Circle Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., The Smith Family"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          <SubmitBtn onClick={handleCreate} disabled={!name.trim() || loading}>
            {loading ? 'Creating...' : 'Create Circle'}
          </SubmitBtn>
        </Form>
      )}

      {mode === 'join' && (
        <Form>
          <Label>Invite Code</Label>
          <CodeInput
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="ABCDEF"
            maxLength={6}
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
            autoFocus
          />
          <SubmitBtn onClick={handleJoin} disabled={code.length < 6 || loading}>
            {loading ? 'Joining...' : 'Join Circle'}
          </SubmitBtn>
        </Form>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
}

const Container = styled.div`
  padding: 0 16px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`;

const BackBtn = styled.button`
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  flex: 1;
`;

const Spacer = styled.div`
  width: 36px;
`;

const ModeSelect = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
`;

const ModeCard = styled.button`
  padding: 24px;
  border-radius: ${({ theme }) => theme.radius.large};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  text-align: left;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const ModeEmoji = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const ModeTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const ModeDesc = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Form = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Input = styled.input`
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  font-size: 16px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const CodeInput = styled(Input)`
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 6px;
  font-family: 'SF Mono', 'Menlo', monospace;
  text-transform: uppercase;
`;

const SubmitBtn = styled.button`
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 17px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 14px;
  text-align: center;
  margin-top: 12px;
`;
